// https://next.vuex.vuejs.org/guide/typescript-support.html#typing-store-property-in-vue-component
import { InjectionKey, State } from "vue";
import { createStore, Store, useStore as baseUseStore } from "vuex";
import { useStorage } from "@vueuse/core";
import { LoginState, PreviousLoginState } from "@/store/vuex";
import { Menu } from "@/features/menu/Menu";
import { repository as menuRepository } from "@/features/menu/configs/MenuConfig";
import { sortBySeq } from "@/libs/Sort";
import { ApiRepository } from "@/repositories/ApiRepository";
import { i18n } from "@/i18n";
import router from "@/router";
import { useRouter } from "vue-router";
// import { saveTokens, getAccessToken, getRefreshToken } from "@/utils/tokenManager";

// define injection key
export const key: InjectionKey<Store<State>> = Symbol();

const loginSession = useStorage(
  "login-session",
  {
    state: "anonymous",
  } as LoginState,
  window.localStorage
);

const previousLoginSession = useStorage("previous-login-session", {} as PreviousLoginState, window.localStorage);
const menu = useStorage("menu", [] as Menu[], window.localStorage);
const menuFav = useStorage("menuFav", [] as Menu[], window.localStorage);

const locale = useStorage("locale", "ko", window.localStorage);
const focusedHeaderId = useStorage("focusedHeaderId", "", window.localStorage);

const mapMenu = (m: Menu) => ({
  name: m.menuName || m.menuNm,
  route:
    m.menuAddress || m.menuUrl
      ? {
          path: m.menuAddress || m.menuUrl,
          meta: {
            currentMenu: m.menuLvl === 1 ? m.menuId : m.upperMenuId,
            icon: m.menuIcon,
          },
        }
      : null,
  seq: m.menuPriority || m.sortSeq,
  id: m.menuId,
  icon: m.menuIcon,
  type: m.menuType,
  desc: m.menuDescription || m.menuDesc,
  favorYn: m.favorYn || "N",
  parentMenuNm: m.parentMenuNm,
});

export const store = createStore<State>({
  state: {
    loading: false,
    currentMenu: "",
    currentSelectedMenu: "",
    currentSelectedSubMenu: "",
    locale: locale.value ?? "ko",
    cntrCd: "",
    cntrNm: "",
    coCd: "",
    eqpId: "",
    eqpNm: "",
    eqpTypeCd: "",
    portId: "",
    userLvl: "",
    tenantIcon: "",
    tenantColor: "",
    toteRegex: "",
    toteUnavblMin: 0,
    mainScreenId: "",
    parentEqpId: "",
    isLnbCollapsed: false, // LeftLNB 접기/펼치기 상태 추가
  },
  mutations: {
    setLoading(state, value: boolean) {
      state.loading = value;
    },
    login(state, v) {
      const { accessToken: _at, refreshToken: _rt, ...otherData } = v?.data || {};

      loginSession.value = {
        ...otherData,
        state: v?.data?.userId ? "LoggedIn" : "anonymous",
      };

      state.eqpId = v?.data?.eqpId;
      state.eqpNm = v?.data?.eqpNm;
      state.eqpTypeCd = v?.data?.eqpTypeCd;
      state.portId = v?.data?.portId;
      state.coCd = v?.data?.coCd;
      state.cntrCd = v?.data?.cntrCd;
      state.cntrNm = v?.data?.cntrNm;
      state.userLvl = v?.data?.userLvl;
      state.tenantIcon = v?.data?.tenantIcon;
      state.tenantColor = v?.data?.tenantColor;
      state.toteRegex = v?.data?.toteRegex || "";
      state.toteUnavblMin = v?.data?.toteUnavblMin || 0;
      state.parentEqpId = v?.data?.parentEqpId;

      // Save tokens to localStorage if present in login response
      // Check both response.data and response directly for tokens
      const accessToken = v?.data?.accessToken || v?.accessToken;
      const refreshToken = v?.data?.refreshToken || v?.refreshToken;
      const expiresIn = v?.data?.expiresIn || v?.expiresIn;

      if (accessToken || refreshToken) {
        // saveTokens({
        //   accessToken: accessToken || "",
        //   refreshToken: refreshToken,
        //   expiresIn: expiresIn,
        // });
      }

      // 로그인 응답에서 메뉴 데이터가 있으면 바로 저장
      if (v?.data?.roleMenuList && v.data.roleMenuList.length > 0) {
        menu.value = v.data.roleMenuList.map((m: Menu) => ({
          ...m,
          // 백엔드 MenuEntity와 매칭
          menuId: m.menuId,
          menuLvl: m.upperMenuId && m.upperMenuId !== null ? 2 : 1, // upperMenuId가 있으면 2, 없으면 1
          menuNm: m.menuNm,
          menuDesc: m.menuDesc,
          menuUrl: m.menuUrl,
          sortSeq: m.sortSeq,
          parentId: m.parentId,
          useYn: m.useYn ?? "Y",
          menuIcon: m.menuIcon ?? "",
          menuType: m.menuType ?? "",
          treeId: m.treeId ?? m.menuId?.toString() ?? "",
        }));
      }
    },
    JWT(state, v) {
      // localStorage에 저장하지 않음
      // loginSession.value.accessToken = v;
    },
    refreshToken(state, v) {
      // localStorage에 저장하지 않음
    },
    restoreLoginSession(state, sessionData) {
      try {
        // localStorage에서 로그인 세션 복원
        loginSession.value = {
          ...sessionData,
          state: sessionData.state || "LoggedIn",
          accessToken: "",
          refreshToken: "",
        };

        // state 값들도 복원
        state.eqpId = sessionData.eqpId || "";
        state.eqpNm = sessionData.eqpNm || "";
        state.eqpTypeCd = sessionData.eqpTypeCd || "";
        state.portId = sessionData.portId || "";
        state.coCd = sessionData.coCd || "";
        state.cntrCd = sessionData.cntrCd || "";
        state.cntrNm = sessionData.cntrNm || "";
        state.userLvl = sessionData.userLvl || "";
        state.tenantIcon = sessionData.tenantIcon || "";
        state.tenantColor = sessionData.tenantColor || "";
        state.toteRegex = sessionData.toteRegex || "";
        state.toteUnavblMin = sessionData.toteUnavblMin || 0;
        state.parentEqpId = sessionData.parentEqpId || "";

        console.log("✅ 로그인 세션 복원 완료:", loginSession.value);
      } catch (error) {
        console.error("❌ 로그인 세션 복원 실패:", error);
        // 복원 실패 시 세션 데이터 정리
        localStorage.removeItem("login-session");
        localStorage.removeItem("menu");
        loginSession.value = {
          userId: "",
          userNm: "",
          state: "anonymous",
          accessToken: "",
          refreshToken: "",
          eqpId: "",
          eqpNm: "",
          eqpTypeCd: "",
          portId: "",
          cntrCd: "",
          cntrNm: "",
          coCd: "",
          userLvl: "",
          tenantIcon: "",
          tenantColor: "",
          toteRegex: "",
          toteUnavblMin: 0,
          mainScreenId: "",
          previousUserId: "",
          previousMainScreenId: "",
          previousCoCd: "",
          previousCntrCd: "",
          parentEqpId: "",
        };
      }
    },
    menu(state, v) {
      menu.value =
        v?.map((m: Menu) => ({
          ...m,
          menuId: m.menuId,
          menuLvl: m.upperMenuId && m.upperMenuId !== null ? 2 : 1, // upperMenuId가 있으면 2, 없으면 1
          menuNm: m.menuNm,
          menuDesc: m.menuDesc,
          menuUrl: m.menuUrl,
          sortSeq: m.sortSeq,
          parentId: m.parentId,
          useYn: m.useYn ?? "Y",
          menuIcon: m.menuIcon ?? "",
          menuType: m.menuType ?? "",
          treeId: m.treeId ?? m.menuId?.toString() ?? "",
        })) ?? [];
    },
    menuFav(state, v) {
      menuFav.value =
        v?.map((m: Menu) => ({
          ...m,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          menuLvl: m.menuLvl ?? m.menuLvl,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          menuNm: m.menuNm ?? m.menuNm,
        })) ?? [];
    },
    mergeRobotControlMenu(state, newMenus: Menu[]) {
      // Get current menu array
      const currentMenus = menu.value || [];

      // Required menuAddress list
      const requiredMenuAddresses = ["/patrol-robot", "/cleanning-robot", "/barista-robot", "/carry-robot"];

      // Filter out menus that have menuAddress in requiredMenuAddresses
      const menusWithoutRobotControl = currentMenus.filter((m: Menu) => {
        const menuAddress = m.menuAddress || m.menuUrl;
        return !menuAddress || !requiredMenuAddresses.includes(menuAddress);
      });

      // Map new menus from API response
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const mappedNewMenus = newMenus.map((m: Menu) => ({
        ...m,
        // Match with backend MenuEntity
        menuId: m.menuId,
        menuLvl: m.upperMenuId && m.upperMenuId !== null ? 2 : 1, // Level 2 if upperMenuId exists, otherwise level 1
        menuNm: m.menuName || m.menuNm,
        menuDesc: m.menuDescription || m.menuDesc,
        menuUrl: m.menuAddress || m.menuUrl,
        menuAddress: m.menuAddress || m.menuUrl,
        sortSeq: m.menuPriority || m.sortSeq || 0,
        menuPriority: m.menuPriority || m.sortSeq || 0, // Preserve menuPriority for sorting
        parentId: m.upperMenuId?.toString() || m.parentId || "",
        useYn: m.useYn ?? "Y",
        menuIcon: m.menuIcon ?? "",
        menuType: m.menuType ?? "",
        treeId: m.treeId ?? m.menuId?.toString() ?? "",
      }));

      // Merge menus: keep non-robot-control menus + add new robot control menus
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const mergedMenus = [...menusWithoutRobotControl, ...mappedNewMenus];

      // Sort all menus by menuPriority or sortSeq
      mergedMenus.sort((a: Menu, b: Menu) => {
        const priorityA = a.menuPriority ?? a.sortSeq ?? 0;
        const priorityB = b.menuPriority ?? b.sortSeq ?? 0;
        return priorityA - priorityB;
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      menu.value = mergedMenus;
    },
    coCd(state, v) {
      state.coCd = v;
      loginSession.value.coCd = v;
    },
    cntrCd(state, v) {
      state.cntrCd = v;
      loginSession.value.cntrCd = v;
    },
    cntrNm(state, v) {
      state.cntrNm = v;
      loginSession.value.cntrNm = v;
    },
    eqpId(state, v) {
      state.eqpId = v;
      loginSession.value.eqpId = v;
    },
    eqpNm(state, v) {
      state.eqpNm = v;
      loginSession.value.eqpNm = v;
    },
    portId(state, v) {
      state.portId = v;
      loginSession.value.portId = v;
    },
    currentMenu(state, v) {
      state.currentMenu = v;
    },
    currentSelectedMenu(state, v) {
      state.currentSelectedMenu = v;
      focusedHeaderId.value = v;
    },
    currentSelectedSubMenu(state, v) {
      state.currentSelectedSubMenu = v;
    },
    toggleLnbCollapsed(state) {
      state.isLnbCollapsed = !state.isLnbCollapsed;
    },
    async locale(state, lang) {
      state.locale = lang;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      i18n.locale = lang;
      locale.value = lang;
      await store.dispatch("changeMenuList");
      document.location.reload();
    },
    mainScreenId(state, v) {
      state.mainScreenId = v;
      loginSession.value.mainScreenId = v;
    },
    setPreviousLoginSession(state, { userId, coCd, cntrCd, mainScreenId }) {
      previousLoginSession.value.userId = userId;
      previousLoginSession.value.coCd = coCd;
      previousLoginSession.value.cntrCd = cntrCd;
      previousLoginSession.value.mainScreenId = mainScreenId;
    },
    checkLoginSession(state) {
      // check previous login session vs current login session
      if (
        loginSession.value.userId != previousLoginSession.value.userId ||
        loginSession.value.coCd != previousLoginSession.value.coCd ||
        loginSession.value.cntrCd != previousLoginSession.value.cntrCd ||
        loginSession.value.mainScreenId != previousLoginSession.value.mainScreenId
      ) {
        localStorage.removeItem("focusedTabId");
        localStorage.removeItem("tabMenuId");
        localStorage.removeItem("focusedHeaderId");
        localStorage.removeItem("menuToggle");
        previousLoginSession.value.userId = loginSession.value.userId;
        previousLoginSession.value.coCd = loginSession.value.coCd;
        previousLoginSession.value.cntrCd = loginSession.value.cntrCd;
        previousLoginSession.value.mainScreenId = loginSession.value.mainScreenId;
      }
    },
    resetAllState(state) {
      // 나머지 상태들을 완전히 초기화
      state.locale = "ko";
      // 기타 추가 상태가 있다면 여기에 추가
    },
    setLogoutState(state) {
      // 로그아웃 상태만 즉시 설정 (레이아웃 전환 트리거)
      state.loading = false;
      state.currentMenu = "";
      state.currentSelectedMenu = "";
      state.currentSelectedSubMenu = "";
      state.cntrCd = "";
      state.cntrNm = "";
      state.coCd = "";
      state.eqpId = "";
      state.eqpNm = "";
      state.eqpTypeCd = "";
      state.portId = "";
      state.userLvl = "";
      state.tenantIcon = "";
      state.tenantColor = "";
      state.toteRegex = "";
      state.toteUnavblMin = 0;
      state.mainScreenId = "";
      state.parentEqpId = "";
      state.isLnbCollapsed = false;

      // 🔴 로그인 세션 상태도 즉시 초기화
      if (loginSession.value) {
        loginSession.value.state = "anonymous";
        loginSession.value.accessToken = "";
        loginSession.value.refreshToken = "";
        loginSession.value.userId = "";
        loginSession.value.userNm = "";
      }
    },
  },
  getters: {
    loading(state) {
      return state.loading;
    },
    menu() {
      return menu.value;
    },
    menuFav() {
      return menuFav.value;
    },
    menuByPath(state, getters) {
      return getters.menu.reduce((acc, cur) => {
        acc[cur.menuUrl] = cur;
        return acc;
      }, {});
    },
    roleCd(state, getters) {
      return getters.loginSession.roleCd;
    },
    kioskYn(state, getters) {
      return getters.loginSession.kioskYn;
    },
    currentMenu(state) {
      return state.currentMenu;
    },
    currentSelectedMenu(state) {
      try {
        // localStorage의 focusedHeaderId를 우선적으로 사용
        const focusedId = focusedHeaderId.value;
        const storeId = state.currentSelectedMenu;

        // 둘 다 있는 경우 focusedId 우선, 없으면 storeId 사용
        if (focusedId) {
          // localStorage와 store 상태가 다르면 store 상태 업데이트
          if (focusedId !== storeId) {
            state.currentSelectedMenu = focusedId;
          }
          return focusedId;
        }

        return storeId;
      } catch (error) {
        console.error("currentSelectedMenu getter 오류:", error);
        return state.currentSelectedMenu || "";
      }
    },
    currentSelectedSubMenu(state) {
      return state.currentSelectedSubMenu;
    },
    isLnbCollapsed(state) {
      return state.isLnbCollapsed;
    },
    currentMenuInstance(state, getters) {
      return getters.headerMenuMap ? getters.headerMenuMap[state.currentMenu] : {};
    },
    headerMenuMap(state, getters) {
      return getters.headerMenu.reduce((acc: Record<string, any>, cur: any) => {
        acc[cur.id] = cur;
        return acc;
      }, {} as Record<string, any>);
    },
    headerMenu() {
      return menu.value
        .filter(m => m.menuLvl === 1)
        .sort(sortBySeq)
        .map(mapMenu);
    },
    locale(state) {
      return state.locale;
    },
    leftMenuMap(state, getters) {
      return getters.leftMenu.reduce((acc: Record<string, any>, cur: any) => {
        if (cur && cur.route && cur.route.path) {
          acc[cur.route.path] = cur;
        }
        return acc;
      }, {} as Record<string, any>);
    },
    selectedLeftMenuMap(state, getters) {
      const result = {} as Record<string, any>;
      getters.selectedLeftMenu.forEach((item: any) => {
        if (item && item.route && item.route.path) {
          result[item.route.path] = item;
        }
      });
      return result;
    },
    currentLevel1Menu(state, getters) {
      const cm = getters.currentLeftMenu?.route?.meta?.currentMenu;
      for (const menuKey of getters.menu) {
        if (menuKey.menuId === cm) {
          return menuKey;
        }
      }
      return null;
    },
    currentLevel1MenuName(state, getters) {
      return getters.currentLevel1Menu?.menuNm;
    },
    currentLeftMenu(state, getters) {
      const c = router.currentRoute.value.path;
      const f = getters.leftMenuMap[router.currentRoute.value.path];
      if (f) {
        return f;
      }
      for (const p of Object.values(getters.leftMenuMap)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!p.children) {
          continue;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        for (const childrenKey of p.children) {
          if (childrenKey.route.path === c) {
            return childrenKey;
          }
        }
      }
      return;
    },
    leftMenu(state) {
      if (menu.value?.length < 1) {
        return [];
      }

      // Level 1 메뉴들 (menuLvl이 1인 것들)을 좌측 메뉴로 표시
      const level1Menus = menu.value.filter(m => m.menuLvl === 1).map(mapMenu);

      // 각 Level 1 메뉴에 Level 2 하위 메뉴들을 children으로 추가
      const menusWithChildren = level1Menus.map(level1Menu => {
        const children = menu.value
          .filter(m => {
            const isLevel2 = m.menuLvl === 2;
            const matchesParent = isLevel2 && m.upperMenuId && m.upperMenuId.toString() === level1Menu.id.toString();
            return matchesParent;
          })
          .map(mapMenu);

        return {
          ...level1Menu,
          children: children.sort((a, b) => (a.seq || 0) - (b.seq || 0)),
        };
      });

      return menusWithChildren.sort((a, b) => (a.seq || 0) - (b.seq || 0));
    },
    selectedLeftMenu(state, getters) {
      if (menu.value?.length < 1) {
        return [];
      }

      // currentSelectedMenu가 설정되어 있지 않으면 빈 배열 반환
      if (!getters.currentSelectedMenu) {
        return [];
      }

      // currentSelectedMenu와 일치하는 Level 1 메뉴 찾기
      const selectedLevel1Menu = menu.value.find(
        m => m.menuLvl === 1 && m.menuId?.toString() === getters.currentSelectedMenu?.toString()
      );

      if (!selectedLevel1Menu) {
        return [];
      }

      // 선택된 Level 1 메뉴를 mapMenu로 변환
      const mappedLevel1Menu = mapMenu(selectedLevel1Menu);

      // 해당 Level 1 메뉴의 Level 2 하위 메뉴들을 children으로 추가
      const children = menu.value
        .filter(m => {
          const isLevel2 = m.menuLvl === 2;
          const matchesParent =
            isLevel2 && m.upperMenuId && m.upperMenuId.toString() === selectedLevel1Menu.menuId.toString();
          return matchesParent;
        })
        .map(mapMenu);

      return [
        {
          ...mappedLevel1Menu,
          children: children.sort((a, b) => (a.seq || 0) - (b.seq || 0)),
        },
      ];
    },
    leftMenuList(state) {
      if (menu.value?.length < 1) {
        return [];
      }
      const pm = menu.value
        .filter(m => m.menuLvl === 1)
        .reduce(
          (acc, cur) => ({
            ...acc,
            [cur.menuId]: { ...mapMenu(cur), children: [] },
          }),
          {}
        );

      menu.value
        .filter(m => m.menuLvl === 2)
        .forEach((item: any) => {
          const accElement = pm[item.parentId];
          if (accElement) {
            accElement.children.push(mapMenu(item));
            accElement.children.sort(sortBySeq);
          }
        });

      return Object.values(pm);
    },
    selectedLeftMenuFav(state) {
      if (menuFav.value?.length < 1) {
        return [];
      }
      const pm = menuFav.value
        .filter(m => m.menuLvl === 0)
        .reduce(
          (acc, cur) => ({
            ...acc,
            [cur.menuId]: { ...mapMenu(cur), children: [] },
          }),
          {}
        );

      menuFav.value
        .filter(m => m.menuLvl === 2)
        .forEach((item: any) => {
          const accElement = pm[item.parentId];
          if (accElement) {
            accElement.children.push(mapMenu(item));
            accElement.children.sort(sortBySeq);
          }
        });

      return Object.values(pm);
    },
    loginSession() {
      return loginSession.value;
    },
    isAuthenticated(state, getters) {
      return getters.loginSession.state === "LoggedIn";
    },
    userId(state, getters) {
      return getters.loginSession?.userId;
    },
    userNm(state, getters) {
      return getters.loginSession?.userNm;
    },
    JWT(state, getters) {
      // return getAccessToken() || "";
    },
    refreshToken(state, getters) {
      // return getRefreshToken() || "";
    },
    cntrCd(state, getters) {
      return state.cntrCd || getters.loginSession?.cntrCd;
    },
    cntrNm(state, getters) {
      return state.cntrNm || getters.loginSession?.cntrNm;
    },
    coCd(state, getters) {
      return state.coCd || getters.loginSession?.coCd;
    },
    eqpId(state, getters) {
      return state.eqpId || getters.loginSession?.eqpId;
    },
    eqpNm(state, getters) {
      return state.eqpNm || getters.loginSession?.eqpNm;
    },
    eqpTypeCd(state, getters) {
      return state.eqpTypeCd || getters.loginSession?.eqpTypeCd;
    },
    appNm(state, getters) {
      return getters.loginSession?.appNm;
    },
    portId(state, getters) {
      return state.portId || getters.loginSession?.portId;
    },
    tenantIcon(state, getters) {
      return state.tenantIcon || getters.loginSession?.tenantIcon;
    },
    tenantColor(state, getters) {
      return state.tenantColor || getters.loginSession?.tenantColor;
    },
    userLvl(state, getters) {
      return state.userLvl || getters.loginSession?.userLvl;
    },
    currentLeftMenuDesc(state, getters) {
      return getters.currentLeftMenu?.desc;
    },
    currentLeftMenuName(state, getters) {
      return getters.currentLeftMenu?.name;
    },
    currentMenuInstanceName(state, getters) {
      return getters.currentMenuInstance?.name;
    },
    toteRegex(state, getters) {
      return getters.loginSession.toteRegex;
    },
    toteUnavblMin(state, getters) {
      return getters.loginSession.toteUnavblMin;
    },
    mainScreenId(state, getters) {
      return getters.loginSession.mainScreenId;
    },
    parentEqpId(state, getters) {
      return state.parentEqpId || getters.loginSession?.parentEqpId;
    },
  },
  actions: {
    // Loading state actions
    setLoading({ commit }, loading: boolean) {
      commit("setLoading", loading);
    },
    showLoading({ commit }) {
      commit("setLoading", true);
    },
    hideLoading({ commit }) {
      commit("setLoading", false);
    },
    // async actions
    // loadMenu({ commit }) {
    //   menuRepository
    //     .list({
    //       useYn: "Y",
    //     })
    //     .then((menuList) => {
    //       commit("menu", menuList);
    //     });
    // },
    async logout({ getters, commit }) {
      // /logout API 호출 (세션 타임아웃일 경우 실패할 수 있으므로 에러 무시)
      // 쿠키로 토큰 관리하므로 쿠키에 토큰이 있으면 자동으로 포함됨
      // 비동기로 처리하여 페이지 이동을 지연시키지 않음
      const apiRepository = new ApiRepository();
      apiRepository.post("/auth/logout", {
        userId: getters.userId,
      }).catch((error) => {
        // 세션 타임아웃 등으로 토큰이 만료된 경우 실패할 수 있으므로 에러 무시
        // 콘솔에만 기록하고 계속 진행
        console.error("⚠️ 로그아웃 API 에러 (무시됨):", error);
      });
      
      // 1. 이전 로그인 정보 보존 (먼저)
      commit("setPreviousLoginSession", {
        userId: getters.loginSession?.userId,
        coCd: getters.loginSession?.coCd,
        cntrCd: getters.loginSession?.cntrCd,
        mainScreenId: getters.loginSession?.mainScreenId,
      });

      // 2. 로그인 상태만 변경 (레이아웃 전환 트리거)
      commit("setLogoutState");

      // 3. sessionStorage 정리
      sessionStorage.removeItem("token-status");

      // 4. localStorage 정리 (로그인 관련 데이터)
      localStorage.removeItem("login-session");
      localStorage.removeItem("menu");
      localStorage.removeItem("menuFav");
      localStorage.removeItem("focusedHeaderId");
      localStorage.removeItem("tabMenuId");
      localStorage.removeItem("focusedTabId");
      localStorage.removeItem("menuToggle");

      // 5. 사이트 정보 초기화 추가
      localStorage.removeItem("selectedSite");
      localStorage.removeItem("selectedSiteId");
      localStorage.removeItem("selectedSiteName");

      // 6. URL 관련 데이터 정리 (로그아웃 후 홈 페이지에 남아있는 문제 해결)
      localStorage.removeItem("current_url");
      localStorage.removeItem("original_url");

      // 7. 나머지 상태 초기화는 레이아웃 전환 후 지연 실행
      setTimeout(() => {
        commit("resetAllState");
      }, 100);
    },
    async abnormalTermination({ getters, commit }) {
      const apiRepository = new ApiRepository();
      await apiRepository.post("/common/login/abnormal-termination", {
        userId: getters.userId,
      });

      sessionStorage.removeItem("token-status");
      commit("setPreviousLoginSession", {
        userId: getters.loginSession.userId,
        coCd: getters.loginSession.coCd,
        cntrCd: getters.loginSession.cntrCd,
        mainScreenId: getters.loginSession.mainScreenId,
      });

      commit("login", null);
      commit("menu", []);
    },
    async login({ commit }, data) {
      // 로그인
      commit("login", {
        ...data,
      });

      if (data) {
        sessionStorage.setItem("token-status", "200");
      } else {
        sessionStorage.removeItem("token-status");
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      /* commit("menu", data.roleMenuList); */
    },
    async changeMenuFavList({ commit }) {
      const apiRepository = new ApiRepository();
      const response = await apiRepository.post("/common/user-menu", {
        userId: loginSession.value.userId,
        coCd: loginSession.value.coCd,
        cntrCd: loginSession.value.cntrCd,
        langCd: localStorage.getItem("locale"),
      });

      commit("menuFav", response.data);
    },
    // async changeMenuList({ commit, dispatch }) {
    //   try {
    //     console.log("Fetching menu data...");

    //     // // 단일 API로 메뉴 데이터 받기
    //     // const { data } = await HTTP.post("/common/getUserMenu", {
    //     //   userId: loginSession.value.userId,
    //     //   coCd: loginSession.value.coCd,
    //     //   cntrCd: loginSession.value.cntrCd,
    //     //   langCd: localStorage.getItem("locale"),
    //     // });

    //     // console.log("Menu API response:", data);

    //     // // 백엔드 응답 구조에 맞게 수정
    //     // const menuList = data?.roleMenuList || data?.data || data || [];
    //     // console.log("Processed menu list:", menuList);

    //     // // 메뉴 데이터 저장
    //     // commit("menu", menuList);
    //     // commit("checkLoginSession");

    //     // // 즐겨찾기 메뉴도 함께 로드
    //     // await dispatch("changeMenuFavList");

    //     return menuList.length > 0;
    //   } catch (error) {
    //     console.error("Error fetching menu:", error);
    //     return false;
    //   }
    // },
    async changeMainScreenId({ commit }, payload) {
      switch (payload.level) {
        case "1": {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          commit("coCd", loginSession.value.coCd);
          commit("cntrCd", loginSession.value.cntrCd);
          commit("cntrNm", loginSession.value.cntrNm);
          commit("mainScreenId", payload.level);

          // loginSession에도 mainScreenId 설정
          loginSession.value.mainScreenId = payload.level;

          return true;
        }
        case "2": {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          commit("coCd", payload.coCd);
          commit("cntrCd", payload.cntrCd);
          commit("cntrNm", payload.cntrNm);
          commit("mainScreenId", payload.level);

          // loginSession에도 mainScreenId 설정
          loginSession.value.mainScreenId = payload.level;

          return true;
        }
        case "3": {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          commit("coCd", loginSession.value.coCd);
          commit("cntrCd", loginSession.value.cntrCd);
          commit("cntrNm", loginSession.value.cntrNm);
          commit("mainScreenId", payload.level);

          // loginSession에도 mainScreenId 설정
          loginSession.value.mainScreenId = payload.level;

          return true;
        }
        default:
          return false;
      }
    },
    async fetchAndMergeRobotControlMenu({ commit }, siteId: string) {
      try {
        commit("setLoading", true);
        const apiRepository = new ApiRepository();

        // API call: GET /common/menu/robot-control?siteId={siteId}
        const params = siteId ? { siteId } : {};
        const response = await apiRepository.get<Menu[]>("/common/menu/robot-control", params);

        // Check response data
        if (response && response.data && Array.isArray(response.data)) {
          // Sort menus by menuPriority before merging
          const sortedMenus = [...response.data].sort((a: Menu, b: Menu) => {
            const priorityA = a.menuPriority ?? a.sortSeq ?? 0;
            const priorityB = b.menuPriority ?? b.sortSeq ?? 0;
            return priorityA - priorityB;
          });

          // Call mergeRobotControlMenu mutation with sorted menus
          commit("mergeRobotControlMenu", sortedMenus);
          return true;
        }

        return false;
      } catch (error) {
        console.error("Error fetching robot control menu:", error);
        return false;
      } finally {
        commit("setLoading", false);
      }
    },
  },
});

// define your own `useStore` composition function
export function useStore(): Store<State> {
  return baseUseStore<State>(key);
}
